import { useEffect, useState } from "react";
import { http } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

function StatCard({ label, value, sub }) {
  return (
    <div className="border border-ink bg-paper p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-sub">{label}</p>
      <p className="font-serif-display text-4xl mt-1">{value?.toLocaleString("es-MX") ?? "—"}</p>
      {sub && <p className="text-[11px] text-sub mt-1 font-mono uppercase tracking-widest">{sub}</p>}
    </div>
  );
}

function ListTable({ title, rows, leftKey, rightKey = "count" }) {
  return (
    <div className="border border-ink bg-paper">
      <div className="px-3 py-2 border-b border-ink bg-ink text-paper">
        <p className="font-mono text-[11px] uppercase tracking-widest">{title}</p>
      </div>
      <ul className="divide-y divide-rule">
        {rows.length ? rows.map((r, i) => (
          <li key={i} className="flex items-baseline justify-between px-3 py-2">
            <span className="font-sans text-sm truncate max-w-[80%]" title={r[leftKey]}>{r[leftKey] || "—"}</span>
            <span className="font-mono text-sm font-semibold">{r[rightKey].toLocaleString("es-MX")}</span>
          </li>
        )) : <li className="px-3 py-3 text-sm text-sub">Sin datos.</li>}
      </ul>
    </div>
  );
}

export default function StatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/stats");
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <p className="font-mono text-sm text-sub" data-testid="stats-loading">Cargando estadísticas…</p>;
  }

  return (
    <div data-testid="admin-stats-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-2xl">Tablero del editor</h3>
        <button
          onClick={load}
          className="border border-ink px-3 py-1 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
          data-testid="stats-refresh"
        >
          ↻ Actualizar
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard label="Visitas hoy" value={data.last_24h} sub="últimas 24h" />
        <StatCard label="Únicos hoy" value={data.unique_24h} sub="IPs únicas 24h" />
        <StatCard label="Visitas 7d" value={data.last_7d} />
        <StatCard label="Únicos 7d" value={data.unique_7d} />
        <StatCard label="Visitas 30d" value={data.last_30d} />
        <StatCard label="Total histórico" value={data.total} />
        <StatCard label="Suscriptores" value={data.subscribers_total} sub="newsletter" />
      </div>

      {/* Daily chart */}
      <div className="border border-ink bg-paper p-4" data-testid="stats-chart">
        <p className="font-mono text-[11px] uppercase tracking-widest text-sub mb-3">
          Visitas por día · últimos 14 días
        </p>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.by_day} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
            <CartesianGrid stroke="#E5E1CF" vertical={false} />
            <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: "#4A4A4A" }} />
            <YAxis allowDecimals={false} tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: "#4A4A4A" }} />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "none", color: "#F4F1EB", fontFamily: "Chivo" }}
              labelStyle={{ color: "#F4F1EB", fontFamily: "IBM Plex Mono" }}
            />
            <Line type="monotone" dataKey="count" stroke="#8B0000" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ListTable title="Países (30 días)" rows={data.by_country} leftKey="country" />
        <ListTable title="Ciudades (30 días)" rows={data.by_city.map((r) => ({ ...r, label: `${r.city}, ${r.country}` }))} leftKey="label" />
        <ListTable title="Páginas más vistas (7 días)" rows={data.by_path} leftKey="path" />
        <ListTable title="Referrers (7 días)" rows={data.by_referrer} leftKey="referrer" />
      </div>
    </div>
  );
}
